import { useCallback, useState } from 'react';

import { useTranslation } from '@splitfiesta/i18n';

import { ErrorContext, ErrorState } from '@/types/error.types';

import {
  getErrorMessage,
  handleError,
  isTranslatableError,
} from '@/utils/errorHandler';

interface UseEnhancedErrorOptions {
  namespace?: string;
  initialError?: ErrorState;
}

interface UseEnhancedErrorReturn extends ErrorContext {
  executeAsync: <T>(
    operation: () => Promise<T>,
    options?: {
      onSuccess?: (result: T) => void;
      onError?: (error: unknown) => void;
      loadingState?: {
        setLoading: (loading: boolean) => void;
      };
    }
  ) => Promise<T | undefined>;
  getErrorMessage: () => string;
}

export function useEnhancedError(
  options: UseEnhancedErrorOptions = {}
): UseEnhancedErrorReturn {
  const { namespace, initialError = null } = options;
  const { t } = useTranslation();
  const [error, setError] = useState<ErrorState>(initialError);

  const clearError = useCallback(() => {
    setError(null);
  }, []);

  const setErrorHandler = useCallback((newError: ErrorState) => {
    setError(newError);
  }, []);

  const handleErrorWithContext = useCallback(
    (err: unknown) => {
      const processedError = handleError(err, namespace);
      setError(processedError);
      return processedError;
    },
    [namespace]
  );

  const executeAsync = useCallback(
    async <T>(
      operation: () => Promise<T>,
      options: {
        onSuccess?: (result: T) => void;
        onError?: (error: unknown) => void;
        loadingState?: {
          setLoading: (loading: boolean) => void;
        };
      } = {}
    ): Promise<T | undefined> => {
      const { onSuccess, onError, loadingState } = options;

      try {
        loadingState?.setLoading(true);
        clearError();

        const result = await operation();

        onSuccess?.(result);
        return result;
      } catch (err) {
        const processedError = handleErrorWithContext(err);
        onError?.(processedError);
        return undefined;
      } finally {
        loadingState?.setLoading(false);
      }
    },
    [clearError, handleErrorWithContext]
  );

  const getErrorMessageForContext = useCallback(() => {
    return getErrorMessage(error, t);
  }, [error, t]);

  return {
    error,
    setError: setErrorHandler,
    clearError,
    hasError: error !== null,
    isTranslatable: isTranslatableError(error),
    executeAsync,
    getErrorMessage: getErrorMessageForContext,
  };
}
